import { useEffect, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  DialogContent,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  Typography,
} from '@mui/material';
import COLORS from 'theme/colors';
import { StyledDialog } from 'theme/index';
import { useAuth } from 'utils/AuthContext';
import SiblingAvatar from './SiblingAvatar';
import { useSiblings } from './useSiblings';
import { useSwitchSibling } from './useSwitchSibling';
import { formatSiblingName } from './siblingProfile';
import { clearPatientSelectionPending, getPatientSelectionReason } from './patientSelection';

/**
 * MM-1172: overlay asking a guardian with more than one child profile which child to open, shown
 * while the patient selection flag is pending (see patientSelection.ts): over the dashboard right
 * after sign-in, and over the current page after the PIN that follows an idle timeout. The children
 * are stacked with a radio each and the overlay cannot be dismissed without a choice. Continue opens
 * the chosen child like the MM-1123 switch (CometChat swap, dashboard, "Now viewing" confirmation);
 * after an idle timeout, keeping the child already open just closes the overlay.
 */
const SelectPatientDialog = () => {
  const { user } = useAuth();
  const { siblings, loaded } = useSiblings();
  const switchSibling = useSwitchSibling();
  /** Id of the child whose radio is checked; nothing is preselected. */
  const [selectedId, setSelectedId] = useState<string>('');
  /** True while the switch to the chosen child runs. */
  const [isSwitching, setIsSwitching] = useState(false);

  useEffect(() => {
    // Nothing to choose between any more (e.g. a child was removed): close without asking.
    if (loaded && (siblings?.length ?? 0) <= 1) {
      clearPatientSelectionPending();
    }
  }, [loaded, siblings]);

  const onContinue = async () => {
    const sibling = siblings?.find((s) => String(s.id) === selectedId);
    if (!sibling) {
      return;
    }
    const reason = getPatientSelectionReason();
    if (reason === 'idle' && sibling.id === user?.patientId) {
      clearPatientSelectionPending();
      return;
    }
    setIsSwitching(true);
    await switchSibling(sibling, reason === 'signIn');
    // Cleared last: clearing unmounts this overlay.
    clearPatientSelectionPending();
  };

  return (
    <StyledDialog open fullWidth maxWidth='xs' disableEscapeKeyDown aria-labelledby='select-patient-title'>
      <DialogContent sx={{ px: { xs: 1, sm: 3 } }}>
        <Typography id='select-patient-title' variant='h3' textAlign='center'>
          Select a profile
        </Typography>
        <Typography variant='subtitle1' textAlign='center' mt={1}>
          Your account has more than one child. Which profile would you like to open?
        </Typography>

        {!loaded ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress aria-label='Loading profiles' />
          </Box>
        ) : (
          <FormControl component='fieldset' fullWidth sx={{ mt: 3 }}>
            <FormLabel component='legend' sx={{ position: 'absolute', left: -10000 }}>
              Child profiles
            </FormLabel>
            <RadioGroup
              name='select-patient'
              value={selectedId}
              onChange={(event) => setSelectedId(event.target.value)}
              sx={{ gap: 1.5 }}
            >
              {siblings?.map((sibling) => {
                const checked = selectedId === String(sibling.id);
                return (
                  <FormControlLabel
                    key={sibling.id}
                    value={String(sibling.id)}
                    disabled={isSwitching}
                    control={<Radio />}
                    labelPlacement='start'
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <SiblingAvatar sibling={sibling} siblings={siblings} size={40} />
                        <Typography sx={{ fontWeight: 600, color: COLORS.DarkBlue }}>
                          {formatSiblingName(sibling)}
                        </Typography>
                      </Box>
                    }
                    sx={{
                      m: 0,
                      px: 2,
                      py: 1.5,
                      justifyContent: 'space-between',
                      border: `1px solid ${checked ? COLORS.DarkBlue : COLORS.BorderGray}`,
                      borderRadius: '10px',
                      background: checked ? COLORS.LightBlue : COLORS.White,
                      '& .MuiFormControlLabel-label': { flex: 1 },
                    }}
                  />
                );
              })}
            </RadioGroup>
          </FormControl>
        )}

        <Box textAlign='center' mt={3}>
          <Button
            sx={{ px: 4 }}
            onClick={onContinue}
            disabled={selectedId === '' || isSwitching}
            startIcon={isSwitching ? <CircularProgress size={16} color='inherit' /> : null}
          >
            Continue
          </Button>
        </Box>
      </DialogContent>
    </StyledDialog>
  );
};

export default SelectPatientDialog;
